import React, {useState} from "react";
import {useForm, Controller} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";

/* @mui material UI */
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
/* Reusable component */
import {FormButton} from "components/FormComponents/Button/FormButton";
import {FormTextField} from "components/FormComponents/TextField/FormTextField";
/* Axios generic Repository */
import {getRequest, postRequest, setHeader} from "repository/genericRepository";
import {VERIFYCOSTCENTERNAME_ENDPOINT} from "commonUtils/globalAPISettings";
import {CostCenterVerificationSchema} from "commonUtils/schema";
import {useAppSelector, store} from "../../../../redux/store";
import {isValidObject, loggedMessage} from "commonUtils/utils";
import {
  Alerts,
  AxiosErrorCode,
  CPSConstants,
  CPSMessages,
  HttpStatusCode,
  PageNames,
} from "commonUtils/enumerations";
import {
  HttpException,
  ServiceAuthenticationException,
} from "services/exceptionHandlerService";
import {logMessage} from "services/loggerService";
import FormValidationAlert from "components/FormComponents/Alert/FormValidationAlert";
import DOMPurify from "dompurify";

interface FormValues {
  costCenterName: string;
}

const ConstCenterVerfication: React.FC = () => {
  const {userDetails} = useAppSelector(state => {
    return state.auth;
  });

  const {
    reset,
    control,
    handleSubmit,
    formState: {errors},
  } = useForm<FormValues>({
    resolver: yupResolver(CostCenterVerificationSchema),
  });

  const [loader, setLoader] = useState<boolean>(false);
  const [successmessage, setSuccessMessage] = useState<string>("");
  const [notfound, setNotFound] = useState<string>("");
  const [button1, setButton1] = useState<string>("");
  const [button2, setButton2] = useState<string>("");
  const [showhidebutton, setShowHideButton] = useState<boolean>(false);
  const [disableButton, setDiableButton] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = React.useState<string>("");

  const setCostCenterMessages = (
    isLoad: boolean,
    showHide: boolean,
    notFound: string,
    errMsg: string,
    successMsg: string,
  ) => {
    setLoader(isLoad);
    setShowHideButton(showHide);
    setNotFound(notFound);
    setErrorMsg(errMsg);
    setSuccessMessage(successMsg);
  };
  React.useEffect(() => {
    setDiableButton(false);
    setNotFound("");
    setErrorMsg("");
    setSuccessMessage("");
    setShowHideButton(false);
    reset();
  }, [userDetails.profileId]);

  const onSubmit = async (data: FormValues) => {
    try {
      const {costCenterName} = data;
      const authState = store.getState();
      const APIHeaders = {
        Authorization: `Bearer ${authState.auth?.authDetails?.token}`,
      };
      if (button1 === "handleButtonSearch") {
        logMessage(
          "INFO",
          `User ${userDetails?.userName} has clicked the Cost Center Verification search button.`,
          PageNames.DASHBOARD,
        );
        setCostCenterMessages(true, false, "", "", "");
        const responseData = await getRequest({
          url: `${VERIFYCOSTCENTERNAME_ENDPOINT}?costCenterName=${costCenterName}`,
          headers: setHeader(APIHeaders),
          timeout: 60000,
          params: {},
        });
        if (isValidObject(responseData)) {
          setCostCenterMessages(false, false, "", "", responseData?.message);
        }
        setLoader(false);
      } else if (button2 === "handleButtonSubmit") {
        logMessage(
          "INFO",
          `User ${userDetails?.userName} has clicked the Cost Center Verification submit button.`,
          PageNames.DASHBOARD,
        );
        setCostCenterMessages(true, false, "", "", "");
        const profileId = authState?.auth?.userDetails?.profileId;
        const switchedProfileId =
          authState?.auth?.userDetails?.switchedProfileId;
        const responseData = await postRequest({
          url: `${VERIFYCOSTCENTERNAME_ENDPOINT}`,
          headers: setHeader(APIHeaders),
          timeout: 60000,
          params: {
            costCenterName,
            profileId,
            switchedProfileId,
          },
        });
        if (
          isValidObject(responseData) &&
          responseData.messageCode === 527396
        ) {
          setDiableButton(false);
          setCostCenterMessages(false, false, "", "", responseData?.message);
        }
        setLoader(false);
      }
    } catch (error: any) {
      loggedMessage(error, errorMsg, PageNames.DASHBOARD);
      setLoader(false);
      handleAxiosErrors(error);
    }
  };

  const handleButtonSearch = () => {
    setButton1("handleButtonSearch");
    setButton2("");
  };
  const handleButtonSubmit = () => {
    setButton2("handleButtonSubmit");
    setButton1("");
  };

  const handleCostVerificationMessage = (error: any) => {
    const messageCodeNum: number = error?.response?.data?.messageCode;
    const messages: string = error?.response?.data?.message;
    if (button1 === "handleButtonSearch") {
      if (messageCodeNum === 527392) {
        setDiableButton(true);
        setCostCenterMessages(false, true, messages, "", "");
      } else if (messageCodeNum === 5273910) {
        setDiableButton(true);
        setCostCenterMessages(false, false, messages, "", "");
      }
    } else if (button2 === "handleButtonSubmit") {
      if (messageCodeNum === 527396) {
        setDiableButton(false);
        setCostCenterMessages(false, false, "", messages, "");
      }
    }
  };

  const handleAxiosErrors = (error: any) => {
    const data = {pagename: PageNames.DASHBOARD};
    if (error?.code === AxiosErrorCode.ERR_BAD_REQUEST) {
      if (
        error?.response?.status === HttpStatusCode.UNAUTHORIZED ||
        error?.response?.status === HttpStatusCode.FORBIDDEN
      ) {
        ServiceAuthenticationException(error, data, error?.response?.status);
      } else if (error?.response?.status === HttpStatusCode.BAD_REQUEST) {
        handleCostVerificationMessage(error);
      } else if (error?.response?.status === HttpStatusCode.NOT_FOUND) {
        setErrorMsg(CPSMessages.SERVICE_NOT_FOUND);
      } else if (
        error?.response?.status === HttpStatusCode.INTERNAL_SERVER_ERROR
      ) {
        HttpException(error, data);
        setErrorMsg(CPSMessages.INTERNAL_SERVER_ERROR);
      } else {
        setErrorMsg(error.message);
      }
    } else if (error?.code === AxiosErrorCode.ECONNABORTED) {
      setErrorMsg(CPSMessages.TIMEOUT_ERROR);
    } else if (error?.code === AxiosErrorCode.ERR_NETWORK) {
      setErrorMsg(CPSMessages.NETWORK_ERROR);
    } else {
      handleCostVerificationMessage(error);
    }
  };

  const handleCostCenterChange = () => {
    setDiableButton(false);
  };

  return (
    <>
      <Box
        component="form"
        onSubmit={event => {
          event.preventDefault();
          handleSubmit(onSubmit)(event);
        }}>
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <Typography component="span" className="cost-center-verification">
              Cost Center Verification
            </Typography>
          </Grid>
          <Grid item xs={9}>
            <Controller
              name="costCenterName"
              control={control}
              defaultValue=""
              render={({field: {ref, ...field}}) => (
                <FormTextField
                  {...field}
                  label=""
                  id="costCenterName"
                  name="costCenterName"
                  inputRef={ref}
                  placeholder={"Enter Cost Center Number"}
                  fullWidth
                  autoComplete={"costCenterName"}
                  error={!!errors.costCenterName}
                  helperText={
                    errors.costCenterName ? errors.costCenterName?.message : ""
                  }
                  onChange={e => {
                    field.onChange(e);
                    handleCostCenterChange();
                  }}
                />
              )}
            />
          </Grid>
          <Grid item xs={3}>
            <FormButton
              onClick={handleButtonSearch}
              type="submit"
              variant="contained"
              outlet="Search"
              disabled={disableButton}
            />
          </Grid>
          {showhidebutton && (
            <Grid item xs={4}>
              <FormButton
                onClick={handleButtonSubmit}
                type="submit"
                variant="contained"
                outlet="Submit"
              />
            </Grid>
          )}
          {errorMsg && (
            <FormValidationAlert severity={Alerts.ERROR} message={errorMsg} />
          )}
          <Grid item xs={12}>
            {loader && (
              <Box className="display-flex justify-content-center">
                <CircularProgress size={15} />
              </Box>
            )}
            {Object.keys(errors).length === 0 && successmessage && (
              <Box className="success-message-color">
                {
                  <Box
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(successmessage),
                    }}
                  />
                }
              </Box>
            )}
            {Object.keys(errors).length === 0 && notfound && (
              <Box className="failed-message-color">
                {
                  <Box
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(notfound),
                    }}
                  />
                }
              </Box>
            )}
          </Grid>
        </Grid>
      </Box>
    </>
  );
};
export default ConstCenterVerfication;
